import { useNotificationsQuery, useResolveNotificationMutation } from "@graphql/notifications/Notifications.g";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { useNotification } from "@providers/NotificationProvider/useNotification";

import { Box } from "@design-system/Box/Box";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Resource } from "@design-system/Resource/Resource";
import { Text } from "@design-system/Text/Text";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Tag } from "@design-system/Tag/Tag";

import { routes } from "@pages/routes";
import { getNotificationTypeLabel } from "@utils/notificationTypes";

import classes from "./RecentUnresolvedNotificationsDropdown.module.css";

type Props = {
  count?: number;
};

export const RecentUnresolvedNotificationsDropdown = ({ count }: Props) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  
  const { resolveNotification, invalidateNotifications } = useNotification();
  const resolveNotificationMutation = useResolveNotificationMutation();

  const notificationsQuery = useNotificationsQuery({
    resolvedAt_Isnull: true,
    sortBy: "-created_at",
    first: count || 20,
  });
  const unresolvedNotifications = notificationsQuery.data;

  const totalCount = unresolvedNotifications?.notifications?.totalCount ?? 0;
  const shownCount = unresolvedNotifications?.notifications?.edges.length ?? 0;
  const refetchNotifications = notificationsQuery.refetch;

  const handleNotificationClick = useCallback(
    (clientId: string) => {
      navigate(
        routes.portal.clients.client.notifications.$buildPath({
          params: {
            clientId,
          },
        }),
        { viewTransition: true },
      );
      setIsOpen(false);
      refetchNotifications();
    },
    [refetchNotifications, navigate],
  );

  const handleSeeAllClick = useCallback(() => {
    navigate(routes.portal.notifications.$path(), { viewTransition: true });
    setIsOpen(false);
  }, [navigate]);

  const handleClearAll = useCallback(async () => {
    if (!unresolvedNotifications?.notifications?.edges) return;
    try {
      const promises = unresolvedNotifications.notifications.edges.map(
        (edge) => {
          if (edge?.node?.id) {
            return resolveNotificationMutation.mutateAsync({
              input: { id: edge.node.id },
            });
          }
          return Promise.resolve();
        }
      );
      await Promise.all(promises);
      toast.success("Notifications cleared");
      invalidateNotifications();
    } catch (e) {
      console.error(e);
      toast.error("Failed to clear notifications");
    }
  }, [unresolvedNotifications, resolveNotificationMutation, invalidateNotifications]);

  const handleDismiss = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      await resolveNotification(id);
    },
    [resolveNotification]
  );

  return (
    <>
      <IconButton
        icon="faBell"
        bubbleCount={totalCount > 0 ? totalCount : undefined}
        onClick={() => setIsOpen(true)}
      />
      
      <SlideInPanel
        title="Notifications"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        showActionButtons={true}
        applyButtonText="See all notifications"
        onApply={handleSeeAllClick}
        footerDirection="column"
        extraButtonText="Clear all"
        onExtraButtonClick={handleClearAll}
      >
        <Box direction="column" gap={4}>
          {shownCount && unresolvedNotifications ? (
            unresolvedNotifications.notifications?.edges.map(
              (notificationNode) => {
                if (
                  !notificationNode?.node?.client ||
                  !notificationNode?.node?.application
                ) {
                  return null;
                }

                const mainApp = notificationNode.node.application.resourceGroupApplications?.primaryApplication || notificationNode.node.application;

                return (
                  <Clickable
                    key={notificationNode.node.id}
                    onClick={() =>
                      handleNotificationClick(
                        notificationNode.node!.client!.id
                      )
                    }
                    className={classes.notificationItem}
                    stretch
                  >
                    {notificationNode?.node?.notificationType &&
                    mainApp.resource?.productName ? (
                      <Box direction="row" justify="space-between" align="center" stretch={true} gap={16}>
                        <Resource
                          name={mainApp.resource.productName}
                          subtext={notificationNode?.node?.client?.name}
                          signedLogoUrl={mainApp.resource.publicResource?.signedLogoUrl || ""}
                        />

                        <Box direction="row" align="center" gap={8}>
                          <Tag
                            color="secondary"
                            variant="light"
                            text={getNotificationTypeLabel(
                              notificationNode?.node?.notificationType,
                            )}
                            noWrap
                          />
                          <IconButton
                            icon="faXmark"
                            size="sm"
                            color="textSecondary"
                            onClick={(e) => handleDismiss(e, notificationNode.node!.id)}
                          />
                        </Box>
                      </Box>
                    ) : null}
                  </Clickable>
                );
              }
            )
          ) : (
            <Box>
              <Text color="textSecondary">No unresolved notifications</Text>
            </Box>
          )}
        </Box>
      </SlideInPanel>
    </>
  );
};
